"use client"

import { useState, useEffect } from "react"
import type { Company, CompanyContext } from "@/lib/types"
import { setCompanyId, setImpersonateUserId } from "@/lib/api"

/**
 * Hook for managing company context in a multi-tenant environment
 */
export function useCompany() {
  const [currentCompany, setCurrentCompany] = useState<CompanyContext | null>(null)
  const [companies, setCompanies] = useState<Company[]>([])

  // Load company data from localStorage on mount
  useEffect(() => {
    const storedCompany = localStorage.getItem("current_company")
    const storedCompanies = localStorage.getItem("user_companies")
    
    if (storedCompany) {
      try {
        const company = JSON.parse(storedCompany)
        setCurrentCompany(company)
        // Set the company ID in the API client so API calls include the X-Company-ID header
        setCompanyId(company.company_id)
      } catch (error) {
        console.error("Failed to parse stored company:", error)
      }
    }
    
    if (storedCompanies) {
      try {
        setCompanies(JSON.parse(storedCompanies))
      } catch (error) {
        console.error("Failed to parse stored companies:", error)
      }
    }
  }, [])

  // Switch to a different company
  const switchCompany = async (company: CompanyContext) => {
    setCurrentCompany(company)
    setCompanyId(company.company_id)
    localStorage.setItem("current_company", JSON.stringify(company))
    // Also save this as the last selected company for future logins
    localStorage.setItem("last_selected_company_id", company.company_id)
    
    // If we're on an app detail page, redirect to admin overview before reload
    if (window.location.pathname.startsWith('/admin/apps/')) {
      window.location.href = '/admin'
    } else {
      // Must reload the page when switching companies to refresh user data
      // with new company context. This fetches the new company's apps list
      // from /auth/me endpoint with the updated X-Company-ID header.
      window.location.reload()
    }
  }

  // Clear company context
  const clearCompany = () => {
    setCurrentCompany(null)
    setCompanyId(null)
    localStorage.removeItem("current_company")
  }

  return {
    currentCompany,
    companies,
    switchCompany,
    clearCompany,
  }
}

/**
 * Hook for platform admin impersonation
 */
export function useImpersonation() {
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null)

  // Start impersonating a user
  const startImpersonation = (userId: string) => {
    setIsImpersonating(true)
    setImpersonatedUserId(userId)
    setImpersonateUserId(userId)
  }

  // Stop impersonating
  const stopImpersonation = () => {
    setIsImpersonating(false)
    setImpersonatedUserId(null)
    setImpersonateUserId(null)
  }

  return {
    isImpersonating,
    impersonatedUserId,
    startImpersonation,
    stopImpersonation,
  }
}